import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { FunnelData } from "../types";
import { colorSchemes } from "../constants";
import { FormElementRenderer } from "../FormElementTypes/FormElementRenderer";

interface PreviewFormProps {
  showForm: boolean;
  setShowForm: (show: boolean) => void;
  data: FunnelData;
  colors: typeof colorSchemes.default;
  formValues: Record<string, string>;
  handleInputChange: (elementId: string, value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  submissionLimitReached: boolean;
}

export const PreviewForm = ({ 
  showForm, 
  setShowForm, 
  data, 
  colors,
  formValues,
  handleInputChange,
  handleSubmit,
  isSubmitting,
  submissionLimitReached
}: PreviewFormProps) => {
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Call the original submit handler
      await handleSubmit(e);

      // If Stripe is enabled and payment link is set, redirect to Stripe
      if (data.afterSubmit?.stripeEnabled && data.afterSubmit?.stripePaymentLink) {
        window.location.href = data.afterSubmit.stripePaymentLink;
      } else {
        // Otherwise, redirect to the selected after submit page
        const selectedPage = data.pages.find(page => page.id === data.finishPageId);
        if (selectedPage) {
          window.location.href = `/${selectedPage.slug}`;
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <Dialog open={showForm} onOpenChange={setShowForm}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{data.siteTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {data.formElements.map((element) => (
            <FormElementRenderer
              key={element.id}
              element={element}
              value={formValues[element.id] || ''}
              onChange={(value) => handleInputChange(element.id, value)}
            />
          ))}
          <Button 
            type="submit" 
            className="w-full" 
            style={{ backgroundColor: colors.primary }}
            disabled={isSubmitting || submissionLimitReached}
          >
            {isSubmitting ? "Submitting..." : submissionLimitReached ? "Submission Limit Reached" : "Submit"}
          </Button>
          {submissionLimitReached && (
            <p className="text-sm text-destructive text-center">
              This funnel has reached its free submission limit. Upgrade to collect more submissions.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
